const COMPANY_URL = '/api/companies';
const PROJECT_URL = '/api/projects';
const AGENT_THREAD_URL = '/api/agent-threads';
const AGENT_URL = '/api/agents';

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

// ─── Types ──────────────────────────────────────────────────────────

export type CompanyStatus = 'active' | 'deactivated';

export interface Company {
  id: number;
  name: string;
  type: string;
  description: string | null;
  status: CompanyStatus;
  color: string | null;
  icon: string | null;
  drive_folder_id: string | null;
  drive_account: string | null;
  drive_folder_name: string | null;
  is_meta: number;
  user_id: number;
  created_at: string;
  updated_at: string;
}

export type AgentThreadStatus = 'active' | 'archived';
export type AgentThreadMessageRole = 'user' | 'assistant';
export type ThreadType = 'founder-to-agent' | 'agent-to-agent';

export interface AgentThread {
  id: number;
  company_id: number;
  agent_id: number;
  user_id: number;
  topic: string;
  status: AgentThreadStatus;
  thread_type: ThreadType;
  from_agent_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface AgentThreadMessage {
  id: number;
  thread_id: number;
  role: AgentThreadMessageRole;
  content: string;
  /** JSON array of image file names, null when none (INT-2026-004). */
  attachments?: string | null;
  created_at: string;
}

export interface AgentThreadWithMessages extends AgentThread {
  messages: AgentThreadMessage[];
}

export interface SendAgentThreadMessageResponse {
  userMessage: AgentThreadMessage;
  assistantMessage?: AgentThreadMessage;
  pending?: boolean;
}

// ─── Company API ──────────────────────────────────────────────────

export async function listCompanies(status?: 'active'): Promise<Company[]> {
  const url = status ? `${COMPANY_URL}?status=${status}` : COMPANY_URL;
  return fetchJson<Company[]>(url);
}

export async function getCompany(id: number): Promise<Company> {
  return fetchJson<Company>(`${COMPANY_URL}/${id}`);
}

export async function createCompany(data: { name: string; type?: string; description?: string; color?: string; icon?: string }): Promise<Company> {
  return fetchJson<Company>(COMPANY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateCompany(id: number, data: Partial<Company>): Promise<Company> {
  return fetchJson<Company>(`${COMPANY_URL}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteCompany(id: number): Promise<void> {
  const response = await fetch(`${COMPANY_URL}/${id}`, { method: 'DELETE' });
  if (!response.ok && response.status !== 204) {
    throw new Error(`Delete failed: ${response.status}`);
  }
}

export async function setCompanyDriveFolder(
  companyId: number,
  config: { drive_folder_id: string | null; drive_account: string | null; drive_folder_name: string | null },
): Promise<Company> {
  return updateCompany(companyId, config);
}

// ─── Company Member API ─────────────────────────────────────────

export interface CompanyMember {
  id: number;
  company_id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  role: 'owner' | 'member';
  created_at: string;
}

export async function getCompanyMembers(companyId: number): Promise<CompanyMember[]> {
  return fetchJson<CompanyMember[]>(`${COMPANY_URL}/${companyId}/members`);
}

export async function inviteCompanyMember(companyId: number, email: string): Promise<CompanyMember> {
  return fetchJson<CompanyMember>(`${COMPANY_URL}/${companyId}/members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

export async function removeCompanyMember(companyId: number, userId: number): Promise<void> {
  const response = await fetch(`${COMPANY_URL}/${companyId}/members/${userId}`, { method: 'DELETE' });
  if (!response.ok && response.status !== 204) {
    throw new Error(`Remove member failed: ${response.status}`);
  }
}

// ─── Project Assignment API ────────────────────────────────────────
// NOTE: CompanySpecwrightProject represents a Specwright *code* project that can
// be assigned to a company. Not to be confused with CompanyDataProject in
// company-project-api.ts (data-aggregation projects for dashboards).

export interface CompanySpecwrightProject {
  id: number;
  name: string;
  path: string;
  status: string;
  company_id: number | null;
  sort_order: number;
}

export async function getProjectsByCompany(companyId: number): Promise<CompanySpecwrightProject[]> {
  return fetchJson<CompanySpecwrightProject[]>(`${PROJECT_URL}?companyId=${companyId}`);
}

export async function assignProjectToCompany(projectId: number, companyId: number | null): Promise<CompanySpecwrightProject> {
  return fetchJson<CompanySpecwrightProject>(`${PROJECT_URL}/${projectId}/company`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId }),
  });
}

// ─── Agent Thread API ─────────────────────────────────────────────

export async function listAgentThreads(companyId: number, threadType?: 'founder-to-agent' | 'agent-to-agent'): Promise<AgentThread[]> {
  const params = new URLSearchParams({ company_id: String(companyId) });
  if (threadType) params.set('thread_type', threadType);
  return fetchJson<AgentThread[]>(`${AGENT_THREAD_URL}?${params}`);
}

export async function createAgentThread(data: { company_id: number; agent_id: number; topic: string }): Promise<AgentThread> {
  return fetchJson<AgentThread>(AGENT_THREAD_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function getAgentThread(id: number): Promise<AgentThreadWithMessages> {
  return fetchJson<AgentThreadWithMessages>(`${AGENT_THREAD_URL}/${id}`);
}

export async function deleteAgentThread(id: number): Promise<void> {
  await fetchJson<{ success: boolean }>(`${AGENT_THREAD_URL}/${id}`, {
    method: 'DELETE',
  });
}

export async function sendAgentThreadMessage(
  threadId: number, content: string, attachments: string[] = []
): Promise<SendAgentThreadMessageResponse> {
  return fetchJson<SendAgentThreadMessageResponse>(`${AGENT_THREAD_URL}/${threadId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(attachments.length > 0 ? { content, attachments } : { content }),
  });
}

export async function uploadAgentThreadAttachment(threadId: number, file: File): Promise<{ name: string; mime_type: string }> {
  return fetchJson<{ name: string; mime_type: string }>(`${AGENT_THREAD_URL}/${threadId}/attachments`, {
    method: 'POST',
    headers: { 'Content-Type': file.type },
    body: file,
  });
}

export function agentThreadAttachmentUrl(threadId: number, name: string): string {
  return `${AGENT_THREAD_URL}/${threadId}/attachments/${encodeURIComponent(name)}`;
}

export function parseAgentThreadAttachments(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((n): n is string => typeof n === 'string') : [];
  } catch {
    return [];
  }
}

/** Stops the running reply. Returns the taken-back message, or null when nothing was running (409). */
export async function cancelAgentThreadMessage(
  threadId: number
): Promise<{ content: string; attachments: string[] } | null> {
  const response = await fetch(`${AGENT_THREAD_URL}/${threadId}/cancel`, { method: 'POST' });
  if (response.status === 409) return null;
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error ?? `Request failed: ${response.status}`);
  }
  const body = await response.json() as { cancelled: { content: string; attachments: string[] } };
  return body.cancelled;
}

export async function getThreadPendingStatus(threadId: number): Promise<{ pending: boolean }> {
  return fetchJson<{ pending: boolean }>(`${AGENT_THREAD_URL}/${threadId}/pending`);
}

export async function editAndResendAgentThreadMessage(
  threadId: number, messageId: number, content: string
): Promise<SendAgentThreadMessageResponse> {
  return fetchJson<SendAgentThreadMessageResponse>(
    `${AGENT_THREAD_URL}/${threadId}/messages/${messageId}`,
    { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ content }) }
  );
}

// ─── Agent Team Types ──────────────────────────────────────────────

export type AgentRole = 'ceo' | 'department_lead' | 'employee';
export type AgentStatus = 'active' | 'paused' | 'deactivated';

export interface CompanyAgent {
  id: number;
  name: string;
  role: AgentRole | null;
  parent_agent_id: number | null;
  status: AgentStatus;
  color: string;
  personality_type: string;
  template_id: string | null;
  company_id: number | null;
  created_at: string;
}

export interface AgentTemplate {
  id: string;
  name: string;
  role: AgentRole;
  category: string;
  description: string;
  personality_type: string;
  communication_style: string;
  core_values: string;
  default_color: string;
  tags: string[];
}

export type HireRequestStatus = 'pending' | 'approved' | 'rejected';

export interface HireRequest {
  id: number;
  company_id: number;
  ceo_agent_id: number;
  template_id: string;
  template_name: string;
  specialization: string | null;
  reason: string;
  status: HireRequestStatus;
  rejection_comment: string | null;
  created_agent_id: number | null;
  user_id: number;
  created_at: string;
  updated_at: string;
}

// ─── Agent Team API ────────────────────────────────────────────────

export async function listCompanyAgents(companyId: number): Promise<CompanyAgent[]> {
  return fetchJson<CompanyAgent[]>(`${AGENT_URL}/company/${companyId}`);
}

export async function listAgentTemplates(options?: { companyType?: string; search?: string }): Promise<AgentTemplate[]> {
  const params = new URLSearchParams();
  if (options?.companyType) params.set('company_type', options.companyType);
  if (options?.search) params.set('search', options.search);
  const query = params.toString() ? `?${params.toString()}` : '';
  return fetchJson<AgentTemplate[]>(`${AGENT_URL}/templates${query}`);
}

export async function requestHire(data: {
  company_id: number;
  ceo_agent_id: number;
  template_id: string;
  specialization?: string;
  reason: string;
}): Promise<HireRequest> {
  return fetchJson<HireRequest>(`${AGENT_URL}/hire`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function listHireRequests(companyId?: number): Promise<HireRequest[]> {
  const query = companyId ? `?company_id=${companyId}` : '';
  return fetchJson<HireRequest[]>(`${AGENT_URL}/hire${query}`);
}

export async function approveHireRequest(id: number): Promise<{ agent: CompanyAgent; hireRequest: HireRequest }> {
  return fetchJson<{ agent: CompanyAgent; hireRequest: HireRequest }>(`${AGENT_URL}/hire/${id}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

export async function rejectHireRequest(id: number, comment?: string): Promise<HireRequest> {
  return fetchJson<HireRequest>(`${AGENT_URL}/hire/${id}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ comment }),
  });
}

export async function deactivateAgent(id: number): Promise<CompanyAgent> {
  return fetchJson<CompanyAgent>(`${AGENT_URL}/${id}/deactivate`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

// ─── MCP Tool Configuration API ──────────────────────────────────

export interface McpToolInfo {
  name: string;
  fullName: string;
}

export interface McpToolGroup {
  server: string;
  tools: McpToolInfo[];
}

export async function getAvailableTools(): Promise<McpToolGroup[]> {
  return fetchJson<McpToolGroup[]>(`${AGENT_URL}/available-tools`);
}

export async function setAgentAllowedTools(agentId: number, tools: string[]): Promise<void> {
  await fetchJson<{ success: boolean }>(`${AGENT_URL}/${agentId}/allowed-tools`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tools }),
  });
}

// ─── Person Access Configuration API ──────────────────────────────

export interface PersonSummary {
  id: number;
  full_name: string;
  category: string;
}

export async function getAvailablePersons(): Promise<PersonSummary[]> {
  return fetchJson<PersonSummary[]>(`${AGENT_URL}/available-persons`);
}

export async function setAgentAllowedPersons(agentId: number, personIds: number[]): Promise<void> {
  await fetchJson<{ success: boolean }>(`${AGENT_URL}/${agentId}/allowed-persons`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ personIds }),
  });
}

// ─── Skill Configuration API ──────────────────────────────────────

export async function getAvailableSkills(projectId?: number): Promise<SkillSummary[]> {
  const params = projectId ? `?projectId=${projectId}` : '';
  return fetchJson<SkillSummary[]>(`/api/team/skills${params}`);
}

export interface SkillSummary {
  id: string;
  name: string;
  description: string;
  category: string;
}

export async function setAgentAllowedSkills(agentId: number, skillIds: string[]): Promise<void> {
  await fetchJson<{ success: boolean }>(`${AGENT_URL}/${agentId}/allowed-skills`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ skillIds }),
  });
}

// ─── Strategy API ─────────────────────────────────────────────────

export type StrategyState =
  | 'goal_received'
  | 'clarifying'
  | 'ready'
  | 'strategizing'
  | 'strategy_review'
  | 'executing'
  | 'blocked'
  | 'reporting'
  | 'completed'
  | 'failed';

export interface AgentGoal {
  id: number;
  agent_id: number;
  company_id: number;
  title: string;
  description: string | null;
  source_agent_id: number | null;
  status: string;
  created_at: string;
}

export interface AgentStrategy {
  id: number;
  agent_id: number;
  goal_id: number;
  state: StrategyState;
  strategy_content: string | null;
  clarifying_questions: string | null;
  clarification_answers: string | null;
  execution_report: string | null;
  block_reason: string | null;
  delegation_type: 'goal' | 'task';
  parent_strategy_id: number | null;
  depth: number;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CompanyCeoStrategy {
  strategy: AgentStrategy | null;
  goal: AgentGoal | null;
}

export async function getCompanyCeoStrategies(companyId: number): Promise<CompanyCeoStrategy[]> {
  return fetchJson<CompanyCeoStrategy[]>(`${AGENT_URL}/company/${companyId}/strategy`);
}

export interface PendingStrategy extends AgentStrategy {
  goal_title: string;
  goal_description: string | null;
  company_id: number;
  company_name: string;
  agent_name: string;
}

export async function getPendingApprovalStrategies(): Promise<PendingStrategy[]> {
  return fetchJson<PendingStrategy[]>(`${AGENT_URL}/strategies/pending-approval`);
}

// ─── Strategy Clarification API ──────────────────────────────────

export async function submitStrategyClarification(strategyId: number, answers: string): Promise<AgentStrategy> {
  return fetchJson<AgentStrategy>(`${AGENT_URL}/strategy/${strategyId}/clarify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers }),
  });
}

export async function reviseStrategy(strategyId: number, feedback: string): Promise<AgentStrategy> {
  return fetchJson<AgentStrategy>(`${AGENT_URL}/strategy/${strategyId}/revise`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ feedback }),
  });
}

export async function approveStrategy(strategyId: number): Promise<AgentStrategy> {
  return fetchJson<AgentStrategy>(`${AGENT_URL}/strategy/${strategyId}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

// ─── Strategy Archive/Retry API ────────────────────────────────────

export async function archiveStrategy(strategyId: number): Promise<AgentStrategy> {
  return fetchJson<AgentStrategy>(`${AGENT_URL}/strategy/${strategyId}/archive`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

export async function cancelStrategy(strategyId: number): Promise<AgentStrategy> {
  return fetchJson<AgentStrategy>(`${AGENT_URL}/strategy/${strategyId}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

export async function retryStrategy(strategyId: number): Promise<{ goal: AgentGoal; strategy: AgentStrategy }> {
  return fetchJson<{ goal: AgentGoal; strategy: AgentStrategy }>(`${AGENT_URL}/strategy/${strategyId}/retry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

// ─── Agent-to-Agent Communication API ─────────────────────────────

export async function listAgentToAgentThreads(companyId: number): Promise<AgentThread[]> {
  return fetchJson<AgentThread[]>(`${AGENT_THREAD_URL}?company_id=${companyId}&thread_type=agent-to-agent`);
}

export async function createAgentToAgentThread(data: {
  company_id: number;
  from_agent_id: number;
  to_agent_id: number;
  topic: string;
}): Promise<AgentThread> {
  return fetchJson<AgentThread>(`${AGENT_THREAD_URL}/agent-comm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function getAgentToAgentThread(id: number): Promise<AgentThreadWithMessages> {
  return fetchJson<AgentThreadWithMessages>(`${AGENT_THREAD_URL}/agent-comm/${id}`);
}

export async function sendAgentMessage(threadId: number, content: string, role?: 'user' | 'assistant'): Promise<AgentThreadMessage> {
  return fetchJson<AgentThreadMessage>(`${AGENT_THREAD_URL}/agent-comm/${threadId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content, role }),
  });
}

// ─── Knowledge API ──────────────────────────────────────────────────

export interface CompanyKnowledge {
  id: number;
  company_id: number;
  user_id: number;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
}

export async function getCompanyKnowledge(companyId: number): Promise<CompanyKnowledge[]> {
  return fetchJson<CompanyKnowledge[]>(`${COMPANY_URL}/${companyId}/knowledge`);
}

export async function createCompanyKnowledge(companyId: number, data: { title: string; content: string }): Promise<CompanyKnowledge> {
  return fetchJson<CompanyKnowledge>(`${COMPANY_URL}/${companyId}/knowledge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateCompanyKnowledge(companyId: number, entryId: number, data: { title?: string; content?: string }): Promise<CompanyKnowledge> {
  return fetchJson<CompanyKnowledge>(`${COMPANY_URL}/${companyId}/knowledge/${entryId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteCompanyKnowledge(companyId: number, entryId: number): Promise<void> {
  await fetch(`${COMPANY_URL}/${companyId}/knowledge/${entryId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
}

// ─── Company Board API ───────────────────────────────────────────────

export type BoardColumn = 'open' | 'inProgress' | 'blocked' | 'done';

export interface CompanyBoardItem {
  id: string;
  type: 'strategy' | 'task' | 'founder_ticket' | 'specwright' | 'routine';
  title: string;
  description?: string;
  assignedAgent?: { id: number; name: string; role: string };
  status: string;
  column: BoardColumn;
  priority?: string;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, unknown>;
}

export interface CompanyBoardColumns {
  open: CompanyBoardItem[];
  inProgress: CompanyBoardItem[];
  blocked: CompanyBoardItem[];
  done: CompanyBoardItem[];
}

export async function getCompanyBoard(companyId: number): Promise<CompanyBoardColumns> {
  const result = await fetchJson<{ columns: CompanyBoardColumns }>(`${COMPANY_URL}/${companyId}/board`);
  return result.columns;
}

// ─── Activity Feed API ──────────────────────────────────────────────

export interface CompanyActivityItem {
  id: string;
  type: 'strategy_change' | 'ticket_created' | 'ticket_responded' | 'hire_request' | 'hire_confirmed';
  agentId: number | null;
  agentName: string | null;
  summary: string;
  timestamp: string;
}

export async function getCompanyActivity(companyId: number, limit = 20): Promise<CompanyActivityItem[]> {
  return fetchJson<CompanyActivityItem[]>(`${COMPANY_URL}/${companyId}/activity?limit=${limit}`);
}

// ─── Budget API ─────────────────────────────────────────────────────

export interface CompanyBudget {
  id: number;
  company_id: number;
  user_id: number;
  monthly_limit: number;
  current_usage: number;
  period_start: string;
  created_at: string;
  updated_at: string;
}

export interface BudgetUsageEntry {
  agent_id: number;
  total_calls: number;
}

export interface BudgetWithUsage {
  budget: CompanyBudget | null;
  usageByAgent: BudgetUsageEntry[];
}

export async function getCompanyBudget(companyId: number): Promise<BudgetWithUsage> {
  return fetchJson<BudgetWithUsage>(`${COMPANY_URL}/${companyId}/budget`);
}

export async function setCompanyBudget(companyId: number, monthlyLimit: number): Promise<CompanyBudget> {
  return fetchJson<CompanyBudget>(`${COMPANY_URL}/${companyId}/budget`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ monthly_limit: monthlyLimit }),
  });
}

export async function resetCompanyBudget(companyId: number, newLimit?: number): Promise<CompanyBudget> {
  return fetchJson<CompanyBudget>(`${COMPANY_URL}/${companyId}/budget/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(newLimit !== undefined ? { monthly_limit: newLimit } : {}),
  });
}

// ─── Task API ──────────────────────────────────────────────────────

export async function createTask(companyId: number, data: { title: string; description?: string }): Promise<{ goal: AgentGoal; strategy: AgentStrategy }> {
  return fetchJson<{ goal: AgentGoal; strategy: AgentStrategy }>(`${AGENT_URL}/company/${companyId}/task`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

// ─── CEO Inbox API ─────────────────────────────────────────────────

export interface CeoInboxItem extends AgentStrategy {
  goal_title: string;
  agent_name: string;
  auto_escalated: number;
  company_id: number;
  company_name: string;
}

export async function getCeoInboxItems(companyId: number, companyName: string): Promise<CeoInboxItem[]> {
  const items = await fetchJson<Omit<CeoInboxItem, 'company_id' | 'company_name'>[]>(`${COMPANY_URL}/${companyId}/ceo-inbox`);
  return items.map(item => ({ ...item, company_id: companyId, company_name: companyName }));
}

export async function unblockStrategy(strategyId: number): Promise<AgentStrategy> {
  return fetchJson<AgentStrategy>(`${AGENT_URL}/strategy/${strategyId}/unblock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

// ─── Routine API ───────────────────────────────────────────────────

export async function createRoutine(companyId: number, data: { name: string; schedule: string; prompt: string; agentId?: number }): Promise<unknown> {
  return fetchJson(`${COMPANY_URL}/${companyId}/routine`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

// ─── Agent Activity API ─────────────────────────────────────────────

export type AgentActivityState = 'active' | 'queued' | 'idle' | 'blocked' | 'stuck';

export interface AgentActivityEntry {
  agentId: number;
  agentName: string;
  companyId: number;
  role: string;
  color: string | null;
  state: AgentActivityState;
  currentAction: string | null;
  strategyId: number | null;
  strategyState: string | null;
  goalTitle: string | null;
  startedAt: string | null;
  durationMs: number | null;
  stuckThresholdExceeded: boolean;
}

export interface AgentActivitySnapshot {
  agents: AgentActivityEntry[];
  claudeQueue: { active: number; waiting: number; maxConcurrency: number };
  lastPollAt: string | null;
  timestamp: string;
}

export async function getAgentActivity(): Promise<AgentActivitySnapshot> {
  return fetchJson<AgentActivitySnapshot>(`${AGENT_URL}/activity`);
}

export async function answerRoutineQuestions(companyId: number, routineId: number, answers: string): Promise<unknown> {
  return fetchJson(`${COMPANY_URL}/${companyId}/routine/${routineId}/answer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers }),
  });
}

export async function toggleRoutine(companyId: number, routineId: number): Promise<unknown> {
  return fetchJson(`${COMPANY_URL}/${companyId}/routine/${routineId}/toggle`, {
    method: 'PATCH',
  });
}
